/**
 * OsHandoffRenderer (the 6th typed renderer, ARCH-005 §1/§9, PRD-004 scoping
 * pass §6 TECH-OS-01).
 *
 * Renders the Zod-validated os_handoff payload BODY only (ARCH-005 §3): the
 * README in the client's voice (markdown, via the shared Markdown component)
 * plus the one-line mental model styled as the "say it back" ownership test.
 * The always-on walkthrough video and the downloads block are composed AROUND
 * this body by the OS owner surface (OsHandoffView, TECH-OS-02); they are not
 * this renderer's job, exactly as the meta-note / sources / video are not the
 * per-type renderers' job in the subscription DeliverableLayout.
 *
 * Verbosity (ARCH-005 §5): the README is the deliverable and always shows whole
 * (it is the thing the owner now owns; tightening it would hide the artifact).
 * Verbosity only governs supporting chrome - at 'build_it_with_me' we add the
 * methodology callout. The mental model always shows.
 */

import {
  MethodologyCallout,
  type RendererProps,
} from '../shared';
import { Markdown } from '../Markdown';
import type { OsHandoffContent } from './payload';

export function OsHandoffRenderer({
  payload,
  verbosity,
}: RendererProps<OsHandoffContent>) {
  return (
    <div className="space-y-6">
      <Markdown source={payload.readme_md} />

      <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-linen)]/50 p-4">
        <p className="font-semibold text-[0.7rem] uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
          Say it back
        </p>
        <p className="mt-1.5 font-display text-lg leading-snug text-[color:var(--color-navy)]">
          {payload.mental_model}
        </p>
      </div>

      <MethodologyCallout verbosity={verbosity}>
        <p>
          This system is yours now. The README is the map; the mental model is
          the one line you should be able to say back without looking. When the
          next painful workflow shows up, you extend this the same way we built
          it together.
        </p>
      </MethodologyCallout>
    </div>
  );
}
